/** @format */

import {useTheme} from '@mui/material/styles';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ReferenceLine,
  ResponsiveContainer,
  CartesianGrid,
  Customized,
  LabelList,
} from 'recharts';

import {measureTextWidth} from 'helpers/measureTextWidth';

import {
  YAxisTickProps,
  PlanChartProps,
  DataEntry,
  CustomizedComponentProps,
} from './types';
import YAxisTickSVG from './components/YAxisTickSVG';
import {ChartWrapper} from './styles';
import CustomizedArea from './components/CustomizedArea';
import CustomizedClipPath from './components/CustomizedClipPath';
import {getNiceNumber} from './helpers/getNiceNumber';
import {generateTicks} from './helpers/generateTicks';
import BarShape from './components/BarShape';
import BarLabel from './components/BarLabel';

const BAR_HEIGHT = 20;
const BAR_GAP = 1;
const AXIS_HEIGHT = 70;

function PlanChart({data, onSegmentClick, valueType = 'number'}: PlanChartProps) {
  const theme = useTheme();
  const values = data.map(d => d.value);
  const min = Math.min(...values);
  const max = Math.max(...values);

  const maxAbs = Math.max(Math.abs(min), Math.abs(max));
  const niceDomain = getNiceNumber(maxAbs);
  const domain = [-niceDomain, niceDomain];

  const formatNumber = (v: number) => v.toLocaleString('ru-RU').replace(',', ' ');
  const formatXAxisTick = (v: number) => (v === 0 ? 'Факт' : formatNumber(v));

  const ticks = generateTicks(niceDomain);

  const chartHeight = data.length * (BAR_HEIGHT + BAR_GAP) + AXIS_HEIGHT;

  // Вычисляем максимальную ширину среди всех лейблов с точным измерением
  const maxLabelWidth = Math.max(...data.map(d => measureTextWidth(d.label ?? '', 14, 400)));

  // Вычисляем необходимую ширину для YAxis: иконка + gap + текст
  const iconSize = 16;
  const gap = 8;
  const yAxisWidth = iconSize + gap + maxLabelWidth;

  const handleBarClick = (entry: DataEntry) => {
    if (onSegmentClick && entry?.id) onSegmentClick(entry.id);
  };

  return (
    <ChartWrapper height={chartHeight} data-testid='plan-chart'>
      <ResponsiveContainer>
        <BarChart data={data} layout='vertical' margin={{left: 20}}>
          {/* Создаём clipPath со скруглением, чтобы грид-сетка не выходила за пределы plot area */}
          <Customized
            component={props => {
              const typifiedProps = props as CustomizedComponentProps;
              return <CustomizedClipPath {...typifiedProps} />;
            }}
          />

          <Customized
            component={props => {
              const typifiedProps = props as CustomizedComponentProps;
              return <CustomizedArea {...typifiedProps} />;
            }}
          />

          <g clipPath='url(#rounded-plot-area)'>
            <CartesianGrid
              vertical
              horizontal={false}
              stroke={theme.palette?.semantic_dark?.surface.surface2}
            />
          </g>

          <Bar
            dataKey='value'
            isAnimationActive={false}
            barSize={BAR_HEIGHT}
            onClick={handleBarClick}
            style={{cursor: onSegmentClick ? 'pointer' : 'default'}}
            shape={<BarShape />}
          >
            <LabelList
              dataKey='value'
              content={props => {
                const {x, y, height, value} = props as {
                  x: number;
                  y: number;
                  height: number;
                  value: number;
                };
                return <BarLabel x={x} y={y} height={height} value={value} valueType={valueType} />;
              }}
            />
          </Bar>

          <ReferenceLine
            x={0}
            stroke={theme.palette?.semantic_dark?.surface.onSurface}
            strokeWidth={1}
          />

          <XAxis
            type='number'
            domain={domain}
            ticks={ticks}
            tickFormatter={formatXAxisTick}
            tickMargin={8}
            axisLine={false}
            tickLine={false}
            tick={{
              fontSize: '10px',
              fontWeight: 400,
              fill: theme.palette?.semantic_dark?.surface.onSurfaceLight,
            }}
          />

          <YAxis
            type='category'
            dataKey='label'
            width={yAxisWidth}
            axisLine={false}
            tickLine={false}
            tick={(props: YAxisTickProps) => (
              <YAxisTickSVG
                {...props}
                data={data}
                onSegmentClick={onSegmentClick}
                maxLabelWidth={maxLabelWidth}
                yAxisWidth={yAxisWidth}
              />
            )}
          />
        </BarChart>
      </ResponsiveContainer>
    </ChartWrapper>
  );
}

export default PlanChart;
